import {useCallback} from 'react';
import {useDefaultOnError} from '../useDefaultOnError';
import type {HttpError, Problem, State} from '@synako/halitrek';
import {useTranslation} from 'react-i18next';
import {Button} from '@chakra-ui/react';
import {useCommand} from '@synako/haligator';
import {useNavigate} from 'react-router-dom';
import {routes} from 'ff-common';
import {useClient} from '@/haligator-factory';
import {AjouterAuPanierCommand} from 'api-types/ffconnect';
import {TextToastCommand} from 'ff-common/src/types/TextToastCommand';
import {useLivraisonsPossiblesContext} from 'ff-common';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import NiceModal from '@ebay/nice-modal-react';
import {ModaleErreurDisponibilite} from '@/domains/cadencier/components/ModaleErreurDisponibilite';
import {ModaleErreurStockMarketplace} from '@/domains/cadencier/components/ModaleErreurStockMarketplace';
import {ItemCatalogue2Desc} from 'api-types/ffconnect-hal';

export const useAjouterAuPanierMarketplace = (textToast: TextToastCommand) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();
  const onError = useDefaultOnError(textToast);
  const {livraisonSelectionnee, setSelectedMarketplaceId} =
    useLivraisonsPossiblesContext();
  const livraison_id = livraisonSelectionnee.data.id;

  const client = useClient();
  const {submitAsync, isSubmitting} = useCommand(
    client,
    'c:ajouter-au-panier',
    {onError},
  );

  const submit = useCallback(
    async (
      payload: AjouterAuPanierCommand,
      itemState?: State<ItemCatalogue2Desc>,
    ) => {
      const marketplace_id = payload.marketplace_id;
      let event;
      try {
        event = await submitAsync(payload, {livraison_id});
      } catch (error) {
        // En cas d'erreur (ex: article déjà dans le panier), on affiche le toast
        // et on bascule quand même vers le panier marketplace
        onError(error as HttpError | Problem);
        setSelectedMarketplaceId(marketplace_id ?? null);
        return;
      }
      setSelectedMarketplaceId(marketplace_id ?? null);

      // Vérification de la disponibilité après ajout
      const date_de_livraison =
        event.data.data.livraison.tolede.date_de_livraison;
      const ligneDePanier = event.follow('ligne_de_panier');
      if (itemState?.hasOneLink('stock_marketplace')) {
        try {
          const stockState = await itemState.follow('stock_marketplace').get();
          const stock = stockState.data.stock;
          if (stock < payload.quantite) {
            NiceModal.show(ModaleErreurStockMarketplace, {
              stock,
              ligneDePanier,
            });
            return;
          }
        } catch {
          /* stock_marketplace peut être indisponible */
        }
      } else {
        try {
          const dispo = await event.follow('disponibilite_article').get();
          const tolede_disponibilite = dispo.data.tolede.disponibilite;
          if (
            tolede_disponibilite === 'rupture' ||
            tolede_disponibilite === 'delai_de_prepa_long' ||
            dispo.data.quantite_manquante
          ) {
            NiceModal.show(ModaleErreurDisponibilite, {
              dispo,
              ligneDePanier,
              date_de_livraison,
            });
            return;
          }
        } catch {
          /* disponibilite_article peut ne pas exister */
        }
      }

      const handleNavigate = () => {
        navigate(
          buildClientRoute(routes.client.panier_marketplace, {marketplace_id}),
        );
      };

      textToast(
        [
          t('toast.ajoute_panier_marketplace', {
            marketplace: t(`marketplace.${marketplace_id}`),
          }),
          <Button
            key='voir-panier-marketplace-cta'
            onClick={handleNavigate}
            variant='unstyled'
            fontWeight='bold'
            textDecoration='underline'
          >
            {t('toast.voir_panier')}
          </Button>,
        ],
        {id: 'ajoute-panier-marketplace-toast'},
      );
    },
    [
      livraison_id,
      submitAsync,
      onError,
      setSelectedMarketplaceId,
      navigate,
      buildClientRoute,
      textToast,
      t,
    ],
  );

  return {isSubmitting, submit};
};
